"use client";

import * as React from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import {
  LayoutDashboard,
  Server,
  ClipboardCheck,
  BookOpen,
  Settings,
  ChevronRight,
  Search,
  LogOut,
  User,
  Building2,
} from "lucide-react";
import { authClient } from "@/lib/auth/client";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useActiveOrganisation } from "@/hooks/use-active-organisation";
import { useSession } from "@/hooks/use-session";
import { cn } from "@/lib/utils";
import { CommandPalette, useCommandPalette } from "./command-palette";
import { Logo } from "@/components/ui/logo";

const navigationGroups = [
  {
    label: "Overview",
    items: [
      {
        title: "Dashboard",
        href: "/dashboard",
        icon: LayoutDashboard,
      },
    ],
  },
  {
    label: "Assessment",
    items: [
      {
        title: "Systems",
        href: "/systems",
        icon: Server,
      },
      {
        title: "Assessments",
        href: "/assessments",
        icon: ClipboardCheck,
      },
      {
        title: "Framework",
        href: "/framework",
        icon: BookOpen,
      },
    ],
  },
  {
    label: "Configuration",
    items: [
      {
        title: "Settings",
        href: "/settings/profile",
        icon: Settings,
      },
    ],
  },
];

interface AppShellProps {
  children: React.ReactNode;
}

function AppShellContent({ children }: AppShellProps) {
  const pathname = usePathname();
  const router = useRouter();
  const { data: session } = useSession();
  const { data: activeOrg } = useActiveOrganisation();
  const { setOpen: setCommandPaletteOpen } = useCommandPalette();

  const handleSignOut = async () => {
    try {
      await authClient.signOut();
      // Sign out successful, redirect to login
      window.location.href = '/login';
    } catch (error) {
      console.error('Sign out error:', error);
      // Even if sign out fails, redirect to login
      // The server will handle invalid sessions
      window.location.href = '/login';
    }
  };

  // Get user initials for avatar
  const userInitials = React.useMemo(() => {
    if (!session?.user?.name) return "U";
    const names = session.user.name.split(" ");
    if (names.length === 1) return names[0].charAt(0).toUpperCase();
    return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
  }, [session]);

  // Truncate org name if too long
  const orgDisplayName = React.useMemo(() => {
    if (!activeOrg?.name) return "Organisation";
    return activeOrg.name.length > 24
      ? `${activeOrg.name.substring(0, 24)}...`
      : activeOrg.name;
  }, [activeOrg]);

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-background">
        {/* Desktop & Tablet Sidebar */}
        <Sidebar className="hidden md:flex" collapsible="icon">
          <SidebarHeader className="border-b border-sidebar-border px-3 py-6 group-data-[state=collapsed]:px-2 group-data-[state=collapsed]:py-6 group-data-[state=collapsed]:flex group-data-[state=collapsed]:justify-center">
            <Link href="/dashboard" className="flex items-center justify-center w-full group-data-[state=collapsed]:justify-center">
              <Logo className="w-full h-auto group-data-[state=collapsed]:w-auto group-data-[state=collapsed]:h-12" />
            </Link>
          </SidebarHeader>

          <SidebarContent className="px-3 py-4 group-data-[state=collapsed]:px-2">
            {navigationGroups.map((group) => (
              <SidebarGroup key={group.label}>
                <SidebarGroupLabel className="px-3 text-xs font-medium uppercase tracking-wide text-muted-foreground group-data-[state=collapsed]:hidden">
                  {group.label}
                </SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {group.items.map((item) => {
                      const isActive = pathname === item.href || pathname.startsWith(`${item.href}/`);
                      return (
                        <SidebarMenuItem key={item.href} className="group-data-[state=collapsed]:flex group-data-[state=collapsed]:justify-center">
                          <SidebarMenuButton asChild isActive={isActive} tooltip={item.title} className="group-data-[state=collapsed]:w-auto group-data-[state=collapsed]:px-0">
                            <Link
                              href={item.href}
                              className={cn(
                                "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all",
                                "group-data-[state=collapsed]:w-auto group-data-[state=collapsed]:justify-center group-data-[state=collapsed]:px-2.5",
                                isActive
                                  ? "bg-sidebar-accent text-sidebar-accent-foreground border-l-[3px] border-accent pl-[9px] group-data-[state=collapsed]:border-l-0 group-data-[state=collapsed]:pl-2.5"
                                  : "text-sidebar-foreground hover:bg-sidebar-accent/50 hover:text-sidebar-accent-foreground"
                              )}
                            >
                              <item.icon
                                className={cn(
                                  "h-5 w-5 transition-colors shrink-0",
                                  isActive ? "text-accent" : "text-muted-foreground group-hover:text-accent"
                                )}
                              />
                              <span className="group-data-[state=collapsed]:hidden">{item.title}</span>
                            </Link>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      );
                    })}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            ))}
          </SidebarContent>

          <SidebarFooter className="border-t border-sidebar-border p-4 group-data-[state=collapsed]:p-2 group-data-[state=collapsed]:flex group-data-[state=collapsed]:items-center group-data-[state=collapsed]:justify-center">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="w-full justify-between gap-3 px-3 py-2.5 h-auto hover:bg-sidebar-accent group-data-[state=collapsed]:w-auto group-data-[state=collapsed]:justify-center group-data-[state=collapsed]:px-2.5"
                >
                  <div className="flex items-center gap-3 min-w-0 group-data-[state=collapsed]:flex-none">
                    <Building2 className="h-5 w-5 shrink-0 text-muted-foreground" />
                    <div className="flex flex-col items-start min-w-0 group-data-[state=collapsed]:hidden">
                      <span className="text-sm font-medium text-sidebar-foreground truncate max-w-full">
                        {orgDisplayName}
                      </span>
                      <span className="text-xs text-muted-foreground truncate max-w-full">
                        {activeOrg?.sector || "Organisation"}
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground group-data-[state=collapsed]:hidden" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 sm:w-56">
                <DropdownMenuLabel>
                  <div className="flex items-center gap-3">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={session?.user?.image || undefined} />
                      <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                        {userInitials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col">
                      <span className="text-sm font-medium">{session?.user?.name}</span>
                      <span className="text-xs text-muted-foreground">{session?.user?.email}</span>
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link href="/settings/profile" className="flex items-center gap-2 cursor-pointer">
                    <User className="h-4 w-4" />
                    <span>Profile</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link href="/settings/profile" className="flex items-center gap-2 cursor-pointer">
                    <Settings className="h-4 w-4" />
                    <span>Settings</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link href="/organisations/select" className="flex items-center gap-2 cursor-pointer">
                    <Building2 className="h-4 w-4" />
                    <span>Switch Organisation</span>
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleSignOut}
                  className="text-destructive cursor-pointer"
                >
                  <LogOut className="h-4 w-4" />
                  <span>Sign Out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarFooter>
        </Sidebar>

        {/* Mobile Header */}
        <div className="md:hidden fixed top-0 left-0 right-0 z-50 h-16 flex items-center justify-between border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-4 safe-top">
          <Link href="/dashboard" className="flex items-center gap-2">
            <Logo className="h-10 w-auto shrink-0" />
          </Link>

          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9"
              onClick={() => setCommandPaletteOpen(true)}
            >
              <Search className="h-4 w-4" />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9">
                  <Avatar className="h-7 w-7">
                    <AvatarImage src={session?.user?.image || undefined} />
                    <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                      {userInitials}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 sm:w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">{session?.user?.name}</span>
                    <span className="text-xs text-muted-foreground">{session?.user?.email}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link href="/settings/profile" className="cursor-pointer">Profile</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link href="/settings/profile" className="cursor-pointer">Settings</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link href="/organisations/select" className="cursor-pointer">Switch Organisation</Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleSignOut}
                  className="text-destructive cursor-pointer"
                >
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Main Content Area */}
        <SidebarInset className="flex flex-1 flex-col">
          {/* Desktop Header */}
          <header className="hidden md:flex sticky top-0 z-40 h-16 shrink-0 items-center gap-4 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-6">
            <SidebarTrigger className="h-7 w-7" />

            <div className="flex flex-1 items-center justify-between">
              <div className="flex-1 max-w-md">
                <Button
                  variant="outline"
                  className="w-full justify-start text-muted-foreground hover:text-foreground h-9"
                  onClick={() => setCommandPaletteOpen(true)}
                >
                  <Search className="mr-2 h-4 w-4" />
                  <span className="text-sm">Search...</span>
                  <kbd className="ml-auto pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border border-border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
                    <span className="text-xs">⌘</span>K
                  </kbd>
                </Button>
              </div>

              <div className="flex items-center gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="h-9 gap-2 px-2">
                      <Avatar className="h-7 w-7">
                        <AvatarImage src={session?.user?.image || undefined} />
                        <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                          {userInitials}
                        </AvatarFallback>
                      </Avatar>
                      <span className="hidden lg:inline-block text-sm font-medium">
                        {session?.user?.name}
                      </span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48 sm:w-56">
                    <DropdownMenuLabel>
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">{session?.user?.name}</span>
                        <span className="text-xs text-muted-foreground">{session?.user?.email}</span>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link href="/settings/profile" className="cursor-pointer">
                        <User className="mr-2 h-4 w-4" />
                        Profile
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link href="/settings/profile" className="cursor-pointer">
                        <Settings className="mr-2 h-4 w-4" />
                        Settings
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link href="/organisations/select" className="cursor-pointer">
                        <Building2 className="mr-2 h-4 w-4" />
                        Switch Organisation
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={handleSignOut}
                      className="text-destructive cursor-pointer"
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </header>

          {/* Page Content */}
          <main className="flex-1 overflow-y-auto pt-[60px] md:pt-0">
            <div className="container mx-auto p-6 pb-24 md:pb-6">
              {children}
            </div>
          </main>

          {/* Mobile Bottom Navigation */}
          <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 pb-safe">
            <div className="grid grid-cols-5 gap-1 px-2 py-2">
              {navigationGroups.flatMap((group) =>
                group.items.map((item) => {
                  const isActive = pathname === item.href || pathname.startsWith(`${item.href}/`);
                  return (
                    <Link
                      key={item.href}
                      href={item.href}
                      className={cn(
                        "flex flex-col items-center justify-center gap-1 rounded-lg px-3 py-2.5 text-xs font-medium transition-colors",
                        isActive
                          ? "bg-accent/10 text-accent"
                          : "text-muted-foreground hover:bg-accent/5 hover:text-accent"
                      )}
                    >
                      <item.icon className="h-5 w-5" />
                      <span className="text-xs">{item.title}</span>
                    </Link>
                  );
                })
              )}
            </div>
          </nav>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
}

export function AppShell({ children }: AppShellProps) {
  return (
    <CommandPalette>
      <AppShellContent>{children}</AppShellContent>
    </CommandPalette>
  );
}
